import { Form, Input, Button, Upload, Row, Col, Collapse, Select, message } from "antd"
import { UploadOutlined, EyeOutlined, DeleteOutlined, UserOutlined, FileTextOutlined, CloudUploadOutlined, PaperClipOutlined, PlusOutlined, CloseCircleOutlined } from "@ant-design/icons"
import React, { useEffect } from "react"
import { ISapImportValidationError, ISapSupplierColumn, ISapSupplierPayloadItem } from "@/types"
import { useUserStore } from "@/stores/user/userStore"
import SupplierPreviewCard from "./SupplierPreviewCard"

const TYPE_OF_REQUEST_DESCRIPTIONS: Record<string, string> = {
  Create: "Request to create new master data",
  Extend: "Request to open for another branch",
  Change: "Request to update existing master data",
  Deactivate: "Request to deactivate master data for all units",
}

const NOTE_PLACEHOLDERS: Record<string, string> = {
  Create: "Example: New supplier needed for project or procurement activity",
  Extend: "Example: Open existing supplier for a new branch or company code",
  Change: "Example: Update supplier master data or supporting information",
  Deactivate: "Example: Supplier is no longer active and should be deactivated",
}

export interface AttachmentItem {
  file: File | null
  description: string
}

interface SapSupplierFormProps {
  isEditMode: boolean
  form: any
  file: File | null
  attachmentItems: AttachmentItem[]
  loading: boolean
  canSubmit: boolean
  onFileChange: (info: any) => void
  onAddAttachment: () => void
  onRemoveAttachment: (index: number) => void
  onAttachmentFileChange: (index: number, file: File | null) => void
  onAttachmentDescriptionChange: (index: number, description: string) => void
  onFinish: (values: any) => void
  previewModalOpen: boolean
  previewLoading: boolean
  previewError: string | null
  previewColumns: ISapSupplierColumn[]
  previewItems: ISapSupplierPayloadItem[]
  previewIsValid: boolean
  previewRequiredFields: string[]
  previewValidationErrors: ISapImportValidationError[]
  setPreviewModalOpen: (open: boolean) => void
}

const SapSupplierForm: React.FC<SapSupplierFormProps> = ({
  isEditMode,
  form,
  file,
  attachmentItems,
  loading,
  canSubmit,
  onFileChange,
  onAddAttachment,
  onRemoveAttachment,
  onAttachmentFileChange,
  onAttachmentDescriptionChange,
  onFinish,
  previewModalOpen,
  previewLoading,
  previewError,
  previewColumns,
  previewItems,
  previewIsValid,
  previewRequiredFields,
  previewValidationErrors,
  setPreviewModalOpen,
}) => {
  const { userData } = useUserStore()
  const selectedTypeOfRequest = Form.useWatch("type_of_request", form)
  const notePlaceholder = NOTE_PLACEHOLDERS[selectedTypeOfRequest || ""] || "Example: Explain the business reason or important context for this request"

  useEffect(() => {
    if (!isEditMode && userData) {
      form.setFieldsValue({
        employee_id: userData.employee_id,
        fullname: userData.fullname,
        level: userData.job_level,
        department: userData.organization_name,
      })
    }
  }, [isEditMode, userData, form])

  const handleRemoveFile = () => {
    setPreviewModalOpen(false)
    onFileChange({ file: { status: "removed" } })
  }

  return (
    <>
      <Form form={form} layout="vertical" onFinish={onFinish} style={{ maxWidth: 1000, margin: "0 auto" }}>
        <Collapse defaultActiveKey={["1"]} ghost style={{ marginBottom: 24, background: "#fff", borderRadius: 8 }}>
          <Collapse.Panel
            header={
              <>
                <UserOutlined /> User Information
              </>
            }
            key="1"
          >
            <Row gutter={24}>
              <Col span={12} xs={24} md={12}>
                <Form.Item label="Employee ID" name="employee_id">
                  <Input disabled bordered={false} style={{ backgroundColor: "#f5f5f5" }} />
                </Form.Item>
              </Col>
              <Col span={12} xs={24} md={12}>
                <Form.Item label="Fullname" name="fullname">
                  <Input disabled bordered={false} style={{ backgroundColor: "#f5f5f5" }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12} xs={24} md={12}>
                <Form.Item label="Level" name="level">
                  <Input disabled bordered={false} style={{ backgroundColor: "#f5f5f5" }} />
                </Form.Item>
              </Col>
              <Col span={12} xs={24} md={12}>
                <Form.Item label="Department" name="department">
                  <Input disabled bordered={false} style={{ backgroundColor: "#f5f5f5" }} />
                </Form.Item>
              </Col>
            </Row>
          </Collapse.Panel>
        </Collapse>

        <Collapse defaultActiveKey={["1"]} ghost style={{ marginBottom: 24, background: "#fff", borderRadius: 8 }}>
          <Collapse.Panel
            header={
              <>
                <FileTextOutlined /> Request Details
              </>
            }
            key="1"
          >
            <div style={{ marginBottom: 16, padding: "12px 16px", background: "#faf7f0", border: "1px solid #f0d8a8", borderRadius: 8, color: "#8c6b1f" }}>
              Provide a clear request type and supporting note so approvers can review this request faster.
            </div>
            <Row gutter={24}>
              <Col span={24}>
                <Form.Item
                  label="Type of Request"
                  name="type_of_request"
                  rules={[{ required: true, message: "Please select type of request" }]}
                  extra={TYPE_OF_REQUEST_DESCRIPTIONS[selectedTypeOfRequest || ""] || "Select the request type that best matches the SAP master data action."}
                >
                  <Select placeholder="Select type of request" disabled={loading} size="large" allowClear>
                    <Select.Option value="Create">Create</Select.Option>
                    <Select.Option value="Extend">Open Unit</Select.Option>
                    <Select.Option value="Change">Change</Select.Option>
                    <Select.Option value="Deactivate">Deactivate</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={24}>
              <Col span={24}>
                <Form.Item label="Note" name="note" extra="Optional. Use this field to explain the business reason or any important context for approvers.">
                  <Input.TextArea placeholder={notePlaceholder} disabled={loading} rows={4} />
                </Form.Item>
              </Col>
            </Row>
          </Collapse.Panel>
        </Collapse>

        <Collapse defaultActiveKey={["1"]} ghost style={{ marginBottom: 24, background: "#fff", borderRadius: 8 }}>
          <Collapse.Panel
            header={
              <>
                <CloudUploadOutlined /> Upload Main Document (Excel)
              </>
            }
            key="1"
          >
            <Form.Item name="file" rules={[{ required: true, message: "Please upload a file" }]}>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {!file ? (
                  <Upload.Dragger
                    beforeUpload={file => {
                      const isExcel = file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" || file.type === "application/vnd.ms-excel" || file.name.endsWith(".xls") || file.name.endsWith(".xlsx")
                      if (!isExcel) {
                        message.error("You can only upload Excel file!")
                        return Upload.LIST_IGNORE
                      }
                      return false
                    }}
                    accept=".xls,.xlsx"
                    onChange={onFileChange}
                    fileList={[]}
                    maxCount={1}
                    showUploadList={false}
                    disabled={loading}
                    style={{ padding: 32, background: "#fafafa", border: "1px dashed #d9d9d9", borderRadius: 8 }}
                  >
                    <p className="ant-upload-drag-icon">
                      <CloudUploadOutlined style={{ color: "#1890ff", fontSize: 48 }} />
                    </p>
                    <p className="ant-upload-text">Click or drag file to this area to upload</p>
                    <p className="ant-upload-hint">Support for a single upload. Only .xls and .xlsx files are allowed.</p>
                    <div onClick={e => e.stopPropagation()} style={{ marginTop: 8 }}>
                      <a href="https://bumiresourcesmineralstbk.sharepoint.com/:x:/s/brms-sap/IQAq_mrYd-arSrtfsFKIomHHAWii1eN9t6b1lcmuC4ZHFAQ?e=Hw0rIb" target="_blank" rel="noopener noreferrer" style={{ color: "#1890ff" }}>
                        Download Template
                      </a>
                    </div>
                  </Upload.Dragger>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 16,
                      padding: 16,
                      background: "#f0faff",
                      border: "1px solid #bae7ff",
                      borderRadius: 8,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 12, overflow: "hidden", flex: 1, minWidth: 200 }}>
                      <div
                        style={{
                          minWidth: 40,
                          width: 40,
                          height: 40,
                          background: "#1890ff",
                          borderRadius: 8,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <PaperClipOutlined style={{ fontSize: 20, color: "#fff" }} />
                      </div>
                      <div style={{ overflow: "hidden", flex: 1 }}>
                        <div style={{ fontWeight: 500, color: "#262626", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</div>
                        <div style={{ fontSize: 12, color: "#8c8c8c" }}>{(file.size / 1024).toFixed(2)} KB</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <Button size="small" type="default" icon={<EyeOutlined />} onClick={() => setPreviewModalOpen(!previewModalOpen)} style={{ display: "flex", alignItems: "center" }}>
                        {previewModalOpen ? "Hide" : "Preview"}
                      </Button>
                      <Button size="small" danger type="default" icon={<DeleteOutlined />} onClick={handleRemoveFile} style={{ display: "flex", alignItems: "center" }}>
                        Remove
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </Form.Item>

            {previewModalOpen && (
              <div style={{ marginBottom: 24 }}>
                <SupplierPreviewCard columns={previewColumns} items={previewItems} loading={previewLoading} error={previewError} requiredFields={previewRequiredFields} validationErrors={previewIsValid ? [] : previewValidationErrors} />
              </div>
            )}
          </Collapse.Panel>
        </Collapse>

        <Collapse defaultActiveKey={["1"]} ghost style={{ marginBottom: 24, background: "#fff", borderRadius: 8 }}>
          <Collapse.Panel
            header={
              <>
                <PaperClipOutlined /> Upload Attachments (Optional - Max 5)
              </>
            }
            key="1"
          >
            <div style={{ marginBottom: 16 }}>
              {attachmentItems.map((item, index) => (
                <div
                  key={index}
                  style={{
                    marginBottom: 16,
                    padding: 16,
                    background: "#fafafa",
                    borderRadius: 8,
                    border: "1px solid #f0f0f0",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <span style={{ fontWeight: 500, color: "#262626" }}>Attachment {index + 1}</span>
                    <Button type="text" danger size="small" icon={<CloseCircleOutlined />} onClick={() => onRemoveAttachment(index)} disabled={loading} style={{ display: "flex", alignItems: "center" }}>
                      Remove
                    </Button>
                  </div>

                  <Row gutter={16}>
                    <Col span={24} md={12}>
                      <div style={{ marginBottom: 8 }}>
                        <label style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4, display: "block" }}>File</label>
                        <Upload
                          beforeUpload={file => {
                            onAttachmentFileChange(index, file)
                            return false
                          }}
                          fileList={
                            item.file
                              ? [
                                  {
                                    uid: `${index}`,
                                    name: item.file.name,
                                    status: "done" as const,
                                    size: item.file.size,
                                  },
                                ]
                              : []
                          }
                          onRemove={() => onAttachmentFileChange(index, null)}
                          maxCount={1}
                          disabled={loading}
                        >
                          <Button style={{ display: "flex", alignItems: "center" }} icon={<UploadOutlined />} disabled={loading}>
                            {item.file ? "Change File" : "Select File"}
                          </Button>
                        </Upload>
                      </div>
                    </Col>
                    <Col span={24} md={12}>
                      <div>
                        <label style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4, display: "block" }}>Description</label>
                        <Input.TextArea placeholder="Enter description" value={item.description} onChange={e => onAttachmentDescriptionChange(index, e.target.value)} disabled={loading} rows={3} autoSize={{ minRows: 3, maxRows: 6 }} />
                      </div>
                    </Col>
                  </Row>
                </div>
              ))}
            </div>

            {attachmentItems.length < 5 && (
              <Button type="dashed" icon={<PlusOutlined />} onClick={onAddAttachment} disabled={loading} block style={{ marginBottom: 8 }}>
                Add Attachment ({attachmentItems.length}/5)
              </Button>
            )}

            <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 8 }}>You can add up to 5 attachments. Each attachment can have its own description.</div>
          </Collapse.Panel>
        </Collapse>

        <Form.Item>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
            <Button size="large" onClick={() => window.history.back()}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={loading} disabled={!canSubmit} size="large" style={{ minWidth: 120 }}>
              {isEditMode ? "Update Supplier" : "Create Supplier"}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </>
  )
}

export default SapSupplierForm
